'use client';

import AppImage from '@/components/ui/AppImage';

interface ProductShowcaseProps {
  onGetQuote: () => void;
}

interface ProductFeature {
  label: string;
  description: string;
}

interface Product {
  id: string;
  name: string;
  image: string;
  alt: string;
  features: ProductFeature[];
}

const products: Product[] = [
  {
    id: '1',
    name: 'WL21-COB Standard Series',
    image: '/assets/images/9a2543d7-d3d1-4614-b458-b0ede381310d-1775111343615.png',
    alt: 'WL21-COB Standard Series LED display panel with COB technology for close-viewing applications',
    features: [
      {
        label: 'Display',
        description: 'Wide color gamut, accurate color reproduction for close-viewing.',
      },
      {
        label: 'Efficiency',
        description: '~40% lower power at same brightness; surface temp ≈10 °C lower.',
      },
      {
        label: 'Protection',
        description: 'Front IP54 dust/splash resistance.',
      },
      {
        label: 'Durability',
        description: '100,000-hour lifetime; 10,000:1 contrast ratio.',
      },
    ],
  },
  {
    id: '2',
    name: 'WL31-COB HDR Series',
    image: '/assets/images/image-1775111451109.png',
    alt: 'WL31-COB HDR Series LED display panel with full flip chip COB technology and HDR ultra HD display',
    features: [
      {
        label: 'Full Flip Chip Process',
        description: 'Advanced COB technology for stability and clarity.',
      },
      {
        label: 'HDR Ultra HD Display',
        description: 'Delivers enhanced brightness, contrast, and vivid colors.',
      },
      {
        label: 'High Stability & Protection',
        description: 'Designed for durability and reliable operation.',
      },
      {
        label: 'Energy Efficient',
        description: 'Lower power usage with environmental protection benefits.',
      },
    ],
  },
  {
    id: '3',
    name: 'WL11-3216 Series',
    image: '/assets/images/image-1775111494582.png',
    alt: 'WL11-3216 Series fine pitch LED display panel designed for professional display environments',
    features: [
      {
        label: 'Stability',
        description: 'Designed for reliable performance with fine pitch LED technology.',
      },
      {
        label: 'Clarity',
        description: 'Delivers sharp images with reduced pixel gaps, ideal for close viewing.',
      },
      {
        label: 'Energy Saving',
        description: 'Efficient design helps reduce overall power consumption.',
      },
      {
        label: 'Durability',
        description: 'Built for long-term use in professional display environments.',
      },
    ],
  },
  {
    id: '4',
    name: 'WL21-3216 Series',
    image: '/assets/images/image-1775111683442.png',
    alt: 'WL21-3216 Series ultra-fine pitch LED display panel for control rooms, conference halls, and broadcast studios',
    features: [
      {
        label: 'High Resolution',
        description: 'Ultra-fine pitch for vivid and lifelike visuals.',
      },
      {
        label: 'Seamless Splicing',
        description: 'Panels align precisely to create bezel-free video walls.',
      },
      {
        label: 'Versatility',
        description: 'Suitable for control rooms, conference halls, and broadcast studios.',
      },
      {
        label: 'Efficiency',
        description: 'Optimized for 24/7 operations with reduced power needs.',
      },
    ],
  },
  {
    id: '5',
    name: 'WL21-1516 Series',
    image: '/assets/images/image-1775111768424.png',
    alt: 'WL21-1516 Series fine pitch LED display panel for indoor applications with high brightness and wide color gamut',
    features: [
      {
        label: 'Fine Pitch Design',
        description: 'Supports high-definition content at closer distances.',
      },
      {
        label: 'High Brightness',
        description: 'Wide color gamut ensures vivid color reproduction.',
      },
      {
        label: 'Reliability',
        description: 'Stable structure with enhanced protection against damage.',
      },
      {
        label: 'Flexibility',
        description: 'Ideal for various indoor applications with scalable installation.',
      },
    ],
  },
];

const ProductShowcase = ({ onGetQuote }: ProductShowcaseProps) => {
  return (
    <section className="py-16 md:py-24 bg-background" aria-labelledby="products-heading">
      <div className="container mx-auto px-5 md:px-10">
        <header className="text-center mb-12">
          <h2 id="products-heading" className="font-headline font-bold text-3xl md:text-5xl text-primary mb-4">
            Our Products
          </h2>
          <p className="font-body text-lg md:text-xl text-text-secondary max-w-3xl mx-auto">
            Explore our professional LED display series engineered for performance, efficiency, and reliability.
          </p>
        </header>

        <div className="space-y-8">
          {/* Top row: 2 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.slice(0, 2).map((product) => (
              <article
                key={product.id}
                className="bg-card rounded-2xl overflow-hidden shadow-card flex flex-col"
              >
                <div className="relative h-64 overflow-hidden">
                  <AppImage
                    src={product.image}
                    alt={product.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-headline font-bold text-xl md:text-2xl text-primary mb-4">
                    {product.name}
                  </h3>
                  <ul className="space-y-3 mb-6 flex-1">
                    {product.features.map((feature) => (
                      <li key={feature.label} className="flex gap-2">
                        <span className="font-headline font-semibold text-accent min-w-[90px]">
                          {feature.label}:
                        </span>
                        <span className="font-body text-text-secondary leading-relaxed text-sm">
                          {feature.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={onGetQuote}
                    className="self-start px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-headline font-semibold text-sm hover:bg-primary/90 transition-all duration-250"
                    aria-label={`Get custom quote for ${product.name}`}
                  >
                    Get Custom Quote
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Bottom row: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {products.slice(2, 5).map((product) => (
              <article
                key={product.id}
                className="bg-card rounded-2xl overflow-hidden shadow-card flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <AppImage
                    src={product.image}
                    alt={product.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-headline font-bold text-lg md:text-xl text-primary mb-3">
                    {product.name}
                  </h3>
                  <ul className="space-y-2 mb-5 flex-1">
                    {product.features.map((feature) => (
                      <li key={feature.label} className="flex gap-2">
                        <span className="font-headline font-semibold text-accent text-sm min-w-[80px]">
                          {feature.label}:
                        </span>
                        <span className="font-body text-text-secondary leading-relaxed text-sm">
                          {feature.description}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={onGetQuote}
                    className="self-start px-4 py-2 bg-primary text-primary-foreground rounded-lg font-headline font-semibold text-sm hover:bg-primary/90 transition-all duration-250"
                    aria-label={`Get custom quote for ${product.name}`}
                  >
                    Get Custom Quote
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;